import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as api from '../lib/api';
import { COVERS } from '../lib/media';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Fasting() {
  const [book, setBook] = useState<api.BookListItem | undefined>(undefined);

  useEffect(() => {
    api.books.list().then(list => {
      setBook(list.find(b => b.slug === 'nano-fasting' || b.title?.toLowerCase().includes('nano')));
    }).catch(() => {});
  }, []);
  return (
    <>
      {/* Hero */}
      <section className="hero">
        <HeroVideo className="video-bg" src={VIDEOS.meatGrill} />
        <div className="video-overlay" />
        <div className="container book-page-hero-container">
          <motion.div
            className="hero-content text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-label">Book Two</span>
            <h1>Nano Fasting</h1>
            <p>The Effortless Approach to Extended Fasting. No Willpower Required.</p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Join the Community
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pain Point */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                What If Fasting Could Be Easy?
              </h2>
              <div className="text-body-lg">
                <p className="mb-responsive-lg">
                  They told you it takes discipline. Willpower. Mental toughness. They said you need to "embrace the suck" and push through.
                </p>
                <p className="mb-responsive-lg">
                  They told you to "push through" the hunger. To ignore what your body is screaming at you. To fight yourself until you win. To treat hunger like an enemy to be conquered.
                </p>
                <p className="mb-responsive-lg">
                  And every time you "failed," you blamed yourself. Not enough discipline. Not enough commitment. Not enough <em>something</em>. The cycle repeated: try harder, fail again, blame yourself.
                </p>
                <p className="book-page-emphasis">
                  But what if there's an easier way? What if the struggle was never necessary?
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Shift */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                There's A Different Way
              </h2>
              <div className="text-body-lg">
                <p className="mb-responsive-lg">
                  What if you could extend your fasting window week after week—and barely notice? What if it felt natural instead of forced?
                </p>
                <p className="mb-responsive-lg">
                  What if hunger stopped being your enemy and became something you understood—a signal you could read and respond to intelligently?
                </p>
                <p className="mb-responsive-lg">
                  What if 30 days from now, you looked back and couldn't believe how far you'd come—without it ever feeling hard? Without the suffering. Without the white-knuckling. Without the battle.
                </p>
                <p className="book-page-emphasis">
                  That's exactly what Nano Fasting teaches you. And you'll understand how to make it happen.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Imagine */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Imagine Waking Up 30 Days From Now...</h2>
          </div>
          <div className="grid-3">
            <ImagineCard
              title="Morning Clarity"
              description="Your mind is sharp. No desperate need for breakfast. Just calm, focused energy. You wake up clear, not hungry."
            />
            <ImagineCard
              title="Afternoon Freedom"
              description="3pm rolls around and you're still going strong. No crash. No cravings. No clock-watching. No reaching for snacks out of habit."
            />
            <ImagineCard
              title="Complete Control"
              description="You eat when YOU decide to eat. Food no longer controls your schedule—or your thoughts. You're in charge."
            />
          </div>
        </div>
      </section>

      {/* The Book - Featured */}
      <section className="section section-white">
        <div className="container">
          <motion.div
            className="book-page-section-intro"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label">The Book</span>
            <h2 className="heading-section">Nano Fasting</h2>
          </motion.div>
          <div className="two-col book-page-two-col">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <img
                src={book?.coverUrl || COVERS.nanoFasting}
                alt="Nano Fasting Book"
                className="book-page-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="book-page-feature-heading">
                This Isn't Another Fasting Protocol
              </h3>
              <p className="text-body-lg book-page-body-spaced">
                You've seen the 16:8 guides. The OMAD schedules. The "just skip breakfast" advice that works for about three days before life gets in the way. Those approaches rely on one thing: your willpower. And willpower always runs out.
              </p>
              <p className="text-body-lg book-page-body-spaced">
                Nano Fasting is different. No lectures. No meal timers. No rigid rules that don't fit your life. Instead, you'll learn to read your body's signals and respond intelligently—making fasting feel natural, not forced.
              </p>
              <p className="text-body-lg book-page-emphasis">
                You won't find this approach anywhere else. Included with your community membership.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="section">
        <div className="container">
          <div className="two-col book-page-two-col">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="book-page-for-heading">
                This Is For You If...
              </h3>
              <ul className="book-page-list-plain">
                <ForItem text="You've tried fasting before and couldn't make it stick" />
                <ForItem text="You're tired of fighting your own hunger" />
                <ForItem text="You want more energy, not less" />
                <ForItem text="You're ready for an approach that actually fits your life" />
                <ForItem text="You want mental clarity and focus without the struggle" />
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="book-page-not-for-heading">
                This Is NOT For You If...
              </h3>
              <ul className="book-page-list-plain">
                <ForItem text="You want overnight results without any effort" />
                <ForItem text="You think suffering is the only path to change" />
                <ForItem text="You're not open to a different approach" />
                <ForItem text="You have an active eating disorder" />
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <span className="section-label">What You Get</span>
            <h2>What's Inside</h2>
          </div>
          <div className="grid-2 book-page-grid-md">
            <div className="card">
              <div className="card-content">
                <h3 className="book-page-card-heading">The Book</h3>
                <ul className="book-page-list-plain">
                  <li className="book-page-card-list-item">The Nano Fasting Method</li>
                  <li className="book-page-card-list-item">15 Levels of Fasting Mastery</li>
                  <li className="book-page-card-list-item">How to Know When You've Arrived</li>
                </ul>
              </div>
            </div>
            <div className="card">
              <div className="card-content">
                <h3 className="book-page-card-heading">The Bonus Materials</h3>
                <ul className="book-page-list-plain">
                  <li className="book-page-card-list-item">Quick Start Protocol</li>
                  <li className="book-page-card-list-item">30-Day Tracker & Journal</li>
                  <li className="book-page-card-list-item">Fasting Levels Cheat Sheet</li>
                  <li className="book-page-card-list-item">Electrolyte Guide</li>
                  <li className="book-page-card-list-item">Breaking Fast Protocols</li>
                  <li className="book-page-card-list-item">Troubleshooting Guide</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Questions? We've Got Answers.</h2>
          </div>
          <div className="content-narrow">
            <FAQItem
              question="How is this different from other fasting programs?"
              answer="Most programs give you a schedule and tell you to stick to it. With this, you'll have a method that adapts to YOUR body and YOUR life. You'll understand why it works—not just what to do."
            />
            <FAQItem
              question="What if fasting hasn't worked for me before?"
              answer="That's who this is for. If willpower-based fasting hasn't worked, you need a different approach. You'll have one."
            />
            <FAQItem
              question="Is this safe?"
              answer="This book is educational, not medical advice. Consult your healthcare provider before making dietary changes, especially if you have health conditions."
            />
            <FAQItem
              question="How do I access it?"
              answer="Instant digital access after purchase. Read on any device."
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta py-responsive">
        <div className="container">
          <div className="content-narrow text-center">
            <h2 className="heading-section book-page-cta-heading">
              You Found Your Tribe
            </h2>
            <p className="text-subtitle mb-responsive book-page-cta-subtitle">
              Stop white-knuckling through fasting. Start doing it the easy way.
            </p>
            <p className="text-quote mb-responsive-lg book-page-cta-body">
              When you join, you get the community, all four books, and everything you need to learn, apply, and finally make fasting sustainable.
            </p>
            <Link to="/join" className="btn btn-secondary btn-lg">
              Join the Community Now
            </Link>
            <p className="book-page-cta-fine">
              $97 one-time · Lifetime access · All books included
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function ImagineCard({ title, description }: { title: string; description: string }) {
  return (
    <motion.div
      className="card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="card-content book-page-card-center">
        <h3 className="book-page-imagine-heading">{title}</h3>
        <p className="book-page-imagine-desc">{description}</p>
      </div>
    </motion.div>
  );
}

function ForItem({ text }: { text: string }) {
  return (
    <li className="book-page-list-item">
      {text}
    </li>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'active' : ''}`}>
      <div className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span className="book-page-faq-text">{question}</span>
        <span className="book-page-faq-icon">{isOpen ? '−' : '+'}</span>
      </div>
      {isOpen && <div className="faq-answer book-page-faq-answer">{answer}</div>}
    </div>
  );
}
